// ── Raster overlay providers: which feed serves radar / satellite for the view ──
//
// Radar (all keyless, CORS-open):
//  • IEM NEXRAD N0Q composite (Iowa State) — CONUS, 0.005° (~0.5 km), 5-min,
//    ~2 min latency, explicit timestamps (stable, cacheable tile URLs), four
//    tile hosts. The hi-res US radar.
//  • NOAA nowCOAST MRMS mosaics — Puerto Rico/USVI, Hawaii, Alaska, Guam
//    (1 km, ~4-min) — the US radars outside CONUS.
//  • RainViewer — global fallback. Its free tier (since 2026-01-01) is capped
//    at zoom 7, one palette, no nowcast: tiles above z7 come back as a grey
//    "Zoom Level Not Supported" block, which is the "radar blocks me when I
//    zoom" bug. Declaring maxzoom 7 makes MapLibre overzoom instead.
//
// Satellite:
//  • nowCOAST GOES (NOAA) — CONUS/PACUS sectors, 5-min, ~4 min latency;
//    longwave IR (enhanced client-side, see enhancedIr.ts), water vapor,
//    0.5 km visible. Coverage ends near 15°N / 62°W.
//  • NASA GIBS full disk (GOES-East/West, Himawari) — 10-min, 25–50 min
//    latency, but global + GeoColor. Used for GeoColor and outside nowCOAST.
//  • GIBS carries no water vapour, so outside the nowCOAST sectors WV comes
//    from NOAA/NESDIS's global geostationary mosaic (GMGSI, via nowCOAST):
//    hourly, ~3 km, ~1 h old, drawn under the 5-min GOES water vapour and
//    cut out wherever that layer has data (enhancedIr.ts).

import { enhancedGlobalWvUrl, enhancedIrUrl, enhancedWvUrl, gibsIrUrl, WV_STOPS } from "./enhancedIr";
import { apiUrl } from "../../data/liveApi";

// Draw order, bottom → top (see FrameEngine): ocean fields under the clouds,
// the global mosaic under the rapid satellite feed, the 1-min storm loop over
// the wider satellite view, radar over both, the official surge-flooding map
// above radar, lightning strikes on top.
export type Family = "ocean" | "satbase" | "sat" | "meso" | "radar" | "surge" | "lightning" | "future" | "futuredetail";

export interface Frame {
  key: string; // stable id (provider + time) → layer/source ids
  time: number; // ms UTC
  tiles?: string[]; // raster tile templates…
  tileSize?: 256 | 512;
  maxzoom?: number; // native max zoom; MapLibre overzooms beyond
  bounds?: [number, number, number, number];
  // …or ONE georeferenced image (server-rendered 1-min / ocean-heat frames)
  image?: { url: string; coordinates: [[number, number], [number, number], [number, number], [number, number]] };
  attribution: string;
}

export interface LegendStop { v: number; color: string }
export interface Legend {
  unit: string;
  stops: LegendStop[];
  note?: string;
  log?: boolean; // position stops on a log scale (lightning density spans 0.1…100)
}

export interface FrameSet {
  family: Family;
  provider: string;
  label: string; // "US NEXRAD" / "GOES-East"
  detail: string; // "0.5 km · 5-min"
  cadenceMin: number;
  frames: Frame[]; // oldest → newest
  legend?: Legend;
}

const MIN = 60_000;
const isoMinute = (ms: number) => new Date(ms).toISOString().slice(0, 16) + "Z";

/** Keep the loop window measured back from the NEWEST available frame, not
 *  from now — a feed running 30 min late would otherwise lose a third of it. */
function trimToWindow<T extends { time: number }>(frames: T[], windowMin: number): T[] {
  const newest = frames.length ? frames[frames.length - 1].time : 0;
  return frames.filter((f) => f.time >= newest - windowMin * MIN);
}
const stamp12 = (ms: number) => new Date(ms).toISOString().replace(/[-:T]/g, "").slice(0, 12); // YYYYMMDDHHMI

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const r = await fetch(url);
    return r.ok ? ((await r.json()) as T) : null;
  } catch {
    return null;
  }
}
async function getText(url: string): Promise<string | null> {
  try {
    const r = await fetch(url);
    return r.ok ? await r.text() : null;
  } catch {
    return null;
  }
}

// ── coverage geometry ──
type BBox = [number, number, number, number]; // w, s, e, n
const inBox = (lng: number, lat: number, b: BBox, pad = 0) =>
  lng >= b[0] - pad && lng <= b[2] + pad && lat >= b[1] - pad && lat <= b[3] + pad;

function inPolygon(lng: number, lat: number, ring: [number, number][]) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Share of the viewport (sampled on a grid) that falls inside a polygon. */
function coverageFraction(view: BBox, ring: [number, number][]) {
  let hit = 0, n = 0;
  for (let i = 0; i <= 10; i++) {
    for (let j = 0; j <= 7; j++) {
      const lng = view[0] + ((view[2] - view[0]) * i) / 10;
      const lat = view[1] + ((view[3] - view[1]) * j) / 7;
      n++;
      if (inPolygon(((lng + 540) % 360) - 180, lat, ring)) hit++;
    }
  }
  return hit / n;
}

// ════════════════════════════ RADAR ════════════════════════════

export type RadarSource = "iem" | "nc-carib" | "nc-hawaii" | "nc-alaska" | "nc-guam" | "rainviewer";

const RADAR_BOXES: { id: Exclude<RadarSource, "rainviewer">; box: BBox }[] = [
  { id: "iem", box: [-126, 23, -65, 50] },
  { id: "nc-carib", box: [-70.5, 15, -61.5, 21.5] }, // Puerto Rico / USVI (TJUA)
  { id: "nc-hawaii", box: [-164, 15, -151, 26] },
  { id: "nc-alaska", box: [-176, 50, -126, 72] },
  { id: "nc-guam", box: [140, 9, 150, 18] },
];

/** US radar where the view centre has it, else the global mosaic. 1.5° of
 *  hysteresis so panning along a domain edge doesn't thrash frame sets. */
export function pickRadarSource(lng: number, lat: number, current: RadarSource | null): RadarSource {
  const cur = RADAR_BOXES.find((b) => b.id === current);
  if (cur && inBox(lng, lat, cur.box, 1.5)) return cur.id;
  return RADAR_BOXES.find((b) => inBox(lng, lat, b.box))?.id ?? "rainviewer";
}

// NWS-style reflectivity ramp (IEM's composite_n0q colour table, 5 dBZ steps)
const IEM_LEGEND: Legend = {
  unit: "dBZ",
  stops: [
    { v: 5, color: "#6376a8" }, { v: 15, color: "#60b4d4" }, { v: 20, color: "#43d67e" },
    { v: 30, color: "#0b840e" }, { v: 40, color: "#ffe200" }, { v: 45, color: "#ffac00" },
    { v: 50, color: "#f80000" }, { v: 55, color: "#aa0000" }, { v: 60, color: "#ffeaff" },
    { v: 65, color: "#f960fa" }, { v: 70, color: "#a400f7" },
  ],
};
export const NWS_LEGEND: Legend = {
  unit: "dBZ",
  stops: [
    { v: 5, color: "#04e9e7" }, { v: 15, color: "#0300f4" }, { v: 20, color: "#02fd02" },
    { v: 30, color: "#008e00" }, { v: 35, color: "#fdf802" }, { v: 45, color: "#fd9500" },
    { v: 50, color: "#fd0000" }, { v: 60, color: "#bc0000" }, { v: 65, color: "#f800fd" }, { v: 70, color: "#9854c6" },
  ],
};
// RainViewer free tier = "Universal Blue" only
const RV_LEGEND: Legend = {
  unit: "dBZ",
  stops: [
    { v: 10, color: "#cec087" }, { v: 15, color: "#88ddee" }, { v: 20, color: "#00a3e0" },
    { v: 30, color: "#005588" }, { v: 35, color: "#ffee00" }, { v: 40, color: "#ffaa00" },
    { v: 45, color: "#ff4400" }, { v: 50, color: "#c10000" }, { v: 55, color: "#ffaaff" }, { v: 65, color: "#ffffff" },
  ],
  note: "global mosaic · coarse above zoom 7",
};

const IEM_HOSTS = ["mesonet", "mesonet1", "mesonet2", "mesonet3"];

async function iemFrames(endMs: number, windowMin: number, product: "N0Q" | "N0R" = "N0Q"): Promise<Frame[]> {
  const start = endMs - windowMin * MIN;
  const d = await getJson<{ scans: { ts: string }[] }>(
    `https://mesonet.agron.iastate.edu/json/radar.py?operation=list&product=${product}&radar=USCOMP` +
    `&start=${isoMinute(start)}&end=${isoMinute(endMs)}`,
  );
  return (d?.scans ?? []).map((s) => {
    const t = Date.parse(s.ts);
    const layer = `ridge::USCOMP-${product}-${stamp12(t)}`;
    return {
      key: `iem-${product}-${stamp12(t)}`,
      time: t,
      // four hosts = 4× the browser's per-host connection budget
      tiles: IEM_HOSTS.map((h) => `https://${h}.agron.iastate.edu/cache/tile.py/1.0.0/${layer}/{z}/{x}/{y}.png`),
      tileSize: 256 as const,
      maxzoom: 10,
      bounds: [-126, 23, -65, 50] as BBox,
      attribution: "Radar © Iowa Environmental Mesonet (NEXRAD)",
    };
  });
}

// nowCOAST publishes each layer's valid times in its WMS capabilities
type NcWorkspace = "weather_radar" | "satellite" | "sea_surface_temperature" | "lightning_detection";
const _capsCache = new Map<string, { at: number; xml: Promise<string | null> }>();
function nowcoastCaps(workspace: NcWorkspace) {
  const hit = _capsCache.get(workspace);
  if (hit && Date.now() - hit.at < 2 * MIN) return hit.xml;
  const xml = getText(`https://nowcoast.noaa.gov/geoserver/${workspace}/wms?service=WMS&version=1.3.0&request=GetCapabilities`);
  _capsCache.set(workspace, { at: Date.now(), xml });
  return xml;
}
async function nowcoastTimes(workspace: NcWorkspace, layer: string): Promise<number[]> {
  const xml = await nowcoastCaps(workspace);
  if (!xml) return [];
  const block = xml.split("<Layer").find((b) => b.includes(`<Name>${layer}</Name>`));
  const dim = block?.match(/<Dimension name="time"[^>]*>([^<]*)</);
  return (dim?.[1] ?? "").split(",").map((s) => Date.parse(s.trim())).filter(Number.isFinite).sort((a, b) => a - b);
}
function nowcoastWms(workspace: string, layer: string, time: number, style = "") {
  return `https://nowcoast.noaa.gov/geoserver/${workspace}/wms?service=WMS&version=1.3.0&request=GetMap` +
    `&layers=${layer}&styles=${style}&format=image/png&transparent=true&crs=EPSG:3857&width=512&height=512` +
    `&time=${new Date(time).toISOString()}&bbox={bbox-epsg-3857}`;
}

const NC_RADAR: Record<"nc-carib" | "nc-hawaii" | "nc-alaska" | "nc-guam", { layer: string; label: string; bounds: BBox }> = {
  "nc-carib": { layer: "caribbean_base_reflectivity_mosaic", label: "Puerto Rico radar", bounds: [-90, 10, -60, 25] },
  "nc-hawaii": { layer: "hawaii_base_reflectivity_mosaic", label: "Hawaii radar", bounds: [-164, 15, -151, 26] },
  "nc-alaska": { layer: "alaska_base_reflectivity_mosaic", label: "Alaska radar", bounds: [-180, 50, -126, 72] },
  "nc-guam": { layer: "guam_base_reflectivity_mosaic", label: "Guam radar", bounds: [140, 9, 150, 18] },
};

async function rainviewerFrames(): Promise<Frame[]> {
  const d = await getJson<{ host: string; radar?: { past?: { time: number; path: string }[] } }>(
    "https://api.rainviewer.com/public/weather-maps.json",
  );
  if (!d) return [];
  return (d.radar?.past ?? []).map((f) => ({
    key: `rv-${f.time}`,
    time: f.time * 1000,
    // 512 px tiles = twice the detail per zoom; colour 2 (Universal Blue) is
    // the only scheme the free tier serves; 1_1 = smoothed + snow
    tiles: [`${d.host}${f.path}/512/{z}/{x}/{y}/2/1_1.png`],
    tileSize: 512 as const,
    maxzoom: 7, // free-tier cap — above this MapLibre overzooms instead of fetching error tiles
    attribution: "Radar © RainViewer",
  }));
}

export async function radarFrameSet(source: RadarSource, windowMin: number, endMs = Date.now()): Promise<FrameSet | null> {
  if (source === "iem") {
    const frames = trimToWindow(await iemFrames(endMs, windowMin + 20), windowMin);
    if (frames.length) {
      return { family: "radar", provider: "iem", label: "US NEXRAD", detail: "0.5 km · 5-min", cadenceMin: 5, frames, legend: IEM_LEGEND };
    }
    // IEM is a university service with no SLA — fall back to NOAA's own
    // CONUS mosaic (1 km) rather than leaving the US without radar
    const all = await nowcoastTimes("weather_radar", "conus_base_reflectivity_mosaic");
    const times = all.filter((t) => t >= (all.at(-1) ?? endMs) - windowMin * MIN);
    return times.length
      ? {
          family: "radar", provider: "nc-conus", label: "US radar", detail: "NOAA MRMS · 1 km · 4-min", cadenceMin: 4, legend: NWS_LEGEND,
          frames: times.map((t) => ({
            key: `nc-conus-${t}`, time: t, tiles: [nowcoastWms("weather_radar", "conus_base_reflectivity_mosaic", t)],
            tileSize: 512 as const, maxzoom: 9, bounds: [-130, 20, -60, 55] as BBox, attribution: "Radar: NOAA nowCOAST (MRMS)",
          })),
        }
      : null;
  }
  if (source === "rainviewer") {
    const frames = trimToWindow(await rainviewerFrames(), windowMin);
    return frames.length
      ? { family: "radar", provider: "rainviewer", label: "Global radar", detail: "RainViewer · 10-min", cadenceMin: 10, frames, legend: RV_LEGEND }
      : null;
  }
  const cfg = NC_RADAR[source];
  const all = await nowcoastTimes("weather_radar", cfg.layer);
  const times = all.filter((t) => t >= (all.at(-1) ?? endMs) - windowMin * MIN);
  if (!times.length) return null;
  return {
    family: "radar", provider: source, label: cfg.label, detail: "NOAA MRMS · 1 km · 4-min", cadenceMin: 4, legend: NWS_LEGEND,
    frames: times.map((t) => ({
      key: `${source}-${t}`, time: t, tiles: [nowcoastWms("weather_radar", cfg.layer, t)],
      tileSize: 512 as const, maxzoom: 9, bounds: cfg.bounds, attribution: "Radar: NOAA nowCOAST (MRMS)",
    })),
  };
}

/** Historical radar for a replay moment (IEM archive: N0R 1995+, N0Q 2011+). */
export async function archiveRadarFrameSet(atMs: number): Promise<FrameSet | null> {
  // snap to the 5-min composite grid and take the scan at/just before
  let frames = await iemFrames(atMs + 2 * MIN, 12, "N0Q");
  let product = "N0Q";
  if (!frames.length) { frames = await iemFrames(atMs + 2 * MIN, 12, "N0R"); product = "N0R"; }
  const f = frames.filter((x) => x.time <= atMs + 2.5 * MIN).pop() ?? frames[0];
  if (!f) return null;
  return {
    family: "radar", provider: `iem-archive-${product}`, label: "NEXRAD archive",
    detail: product === "N0Q" ? "0.5 km" : "1 km (pre-2011)", cadenceMin: 5, frames: [f], legend: IEM_LEGEND,
  };
}

// ════════════════════════════ SATELLITE ════════════════════════════

export type SatProduct = "geocolor" | "ir" | "wv" | "vis";
export type SatBird = "east" | "west" | "himawari";
export type SatSource =
  | { kind: "nowcoast"; product: "ir" | "wv" | "vis" }
  | { kind: "gibs"; bird: SatBird; product: "geocolor" | "ir" | "vis" };

interface GibsProduct { layer: string; tms: string; ext: string; zoom: number }
// GIBS returns 404 outside a bird's disk, so the bird follows the view.
// Himawari has no GeoColor on GIBS — its "visible" is the red band.
export const SAT_FLEET: Record<SatBird, { label: string; subLon: number; geocolor: GibsProduct; ir: GibsProduct; vis: GibsProduct }> = {
  east: {
    label: "GOES-East", subLon: -75.2,
    geocolor: { layer: "GOES-East_ABI_GeoColor", tms: "GoogleMapsCompatible_Level7", ext: "jpg", zoom: 7 },
    ir: { layer: "GOES-East_ABI_Band13_Clean_Infrared", tms: "GoogleMapsCompatible_Level6", ext: "png", zoom: 6 },
    vis: { layer: "GOES-East_ABI_Band2_Red_Visible_1km", tms: "GoogleMapsCompatible_Level7", ext: "png", zoom: 7 },
  },
  west: {
    label: "GOES-West", subLon: -137.2,
    geocolor: { layer: "GOES-West_ABI_GeoColor", tms: "GoogleMapsCompatible_Level7", ext: "jpg", zoom: 7 },
    ir: { layer: "GOES-West_ABI_Band13_Clean_Infrared", tms: "GoogleMapsCompatible_Level6", ext: "png", zoom: 6 },
    vis: { layer: "GOES-West_ABI_Band2_Red_Visible_1km", tms: "GoogleMapsCompatible_Level7", ext: "png", zoom: 7 },
  },
  himawari: {
    label: "Himawari", subLon: 140.7,
    geocolor: { layer: "Himawari_AHI_Band3_Red_Visible_1km", tms: "GoogleMapsCompatible_Level7", ext: "png", zoom: 7 },
    ir: { layer: "Himawari_AHI_Band13_Clean_Infrared", tms: "GoogleMapsCompatible_Level6", ext: "png", zoom: 6 },
    vis: { layer: "Himawari_AHI_Band3_Red_Visible_1km", tms: "GoogleMapsCompatible_Level7", ext: "png", zoom: 7 },
  },
};

export function pickBird(centerLon: number, current: SatBird | null): SatBird {
  const dist = (b: SatBird) => Math.abs(((centerLon - SAT_FLEET[b].subLon + 540) % 360) - 180);
  let best: SatBird = "east";
  for (const b of ["west", "himawari"] as SatBird[]) if (dist(b) < dist(best)) best = b;
  // 4° hysteresis: keep the current bird unless the switch is clearly better
  if (current && current !== best && dist(current) - dist(best) < 4) return current;
  return best;
}

// nowCOAST GOES coverage (measured from the layer's alpha): CONUS + PACUS
// sectors, cut off near 15°N and ~62°W — the MDR is outside it.
const NC_GOES_RING: [number, number][] = [
  [-178, 50], [-54.3, 50], [-56.6, 43.4], [-58.4, 37.8], [-59.9, 30.8], [-61.2, 23.8], [-62.1, 15.3],
  [-161.7, 15.3], [-163.3, 23.8], [-165.6, 30.8], [-168.9, 37.8], [-172.6, 43.4], [-176.5, 47.6],
];

/**
 * Rapid (5-min, ~4 min old) nowCOAST imagery when the view sits inside its
 * sectors; the GIBS full disk otherwise (and always for GeoColor). Hysteresis
 * keeps a borderline view from flip-flopping between frame sets.
 */
export function pickSatSource(product: SatProduct, view: BBox, bird: SatBird, current: SatSource | null): SatSource {
  if (product === "geocolor") return { kind: "gibs", bird, product: "geocolor" };
  if (product === "wv") return { kind: "nowcoast", product: "wv" }; // GIBS carries no GOES water vapour
  const frac = coverageFraction(view, NC_GOES_RING);
  const wasRapid = current?.kind === "nowcoast" && current.product === product;
  if (frac >= (wasRapid ? 0.7 : 0.85)) return { kind: "nowcoast", product };
  return { kind: "gibs", bird, product };
}

/** GOES water vapour only covers the nowCOAST sectors — does the view reach
 *  past them (and so need the global mosaic underneath)? */
export function wvNeedsGlobal(view: BBox): boolean {
  return coverageFraction(view, NC_GOES_RING) < 1;
}

export function satSourceKey(s: SatSource) {
  return s.kind === "nowcoast" ? `nc-${s.product}` : `gibs-${s.bird}-${s.product}`;
}

// Band-13 brightness temperature legend (°C) — GIBS's Clean-IR colour table,
// which the enhanced nowCOAST IR reuses so both feeds read identically.
export const IR_LEGEND: Legend = {
  unit: "°C",
  stops: [
    { v: 20, color: "#606060" }, { v: 0, color: "#949494" }, { v: -19, color: "#c5c5c5" },
    { v: -20, color: "#00f2f8" }, { v: -30, color: "#000073" }, { v: -40, color: "#00ea0a" },
    { v: -50, color: "#e6ff00" }, { v: -60, color: "#ff1a00" }, { v: -70, color: "#1a0000" },
    { v: -80, color: "#e6e6e6" }, { v: -90, color: "#8c0d87" },
  ],
  note: "cloud-top temperature · colder = deeper convection · clear sky see-through",
};

export const WV_LEGEND: Legend = {
  unit: "°C",
  stops: WV_STOPS.map(([v, [r, g, b]]) => ({ v, color: `rgb(${r},${g},${b})` })),
  note: "upper-level water vapour · dark/gold = dry · white/blue = moist · red = deep convection",
};

const NC_SAT: Record<"ir" | "wv" | "vis", { layer: string; label: string; detail: string; maxzoom: number; legend?: Legend }> = {
  ir: { layer: "goes_longwave_imagery", label: "GOES IR (rapid)", detail: "2 km · 5-min", maxzoom: 8, legend: IR_LEGEND },
  wv: { layer: "goes_water_vapor_imagery", label: "GOES water vapor", detail: "band 8 · 5-min", maxzoom: 8, legend: WV_LEGEND },
  vis: { layer: "goes_visible_imagery", label: "GOES visible (rapid)", detail: "0.5 km · 5-min · daylight", maxzoom: 10 },
};
const NC_SAT_BOUNDS: BBox = [-179.5, 15, -50.7, 50.6];

// GIBS valid times from a time-WINDOWED DescribeDomains (≈400 bytes; the
// unwindowed all/all.xml is ~1 MB / ~3 s back to 2021). The domain is gappy —
// never generate timestamps.
async function gibsTimes(p: GibsProduct, endMs: number, windowMin: number): Promise<number[]> {
  const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 19) + "Z";
  const xml = await getText(
    `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/1.0.0/${p.layer}/default/${p.tms}/all/` +
    // reach back past GIBS's 25–50 min latency so the window can end at its newest frame
    `${fmt(endMs - (windowMin + 75) * MIN)}--${fmt(endMs + 30 * MIN)}.xml`,
  );
  if (!xml) return [];
  const out: number[] = [];
  for (const m of xml.matchAll(/([0-9T:-]+Z)\/([0-9T:-]+Z)\/PT(\d+)M/g)) {
    const step = Number(m[3]) * MIN;
    for (let t = Date.parse(m[1]); t <= Date.parse(m[2]); t += step) out.push(t);
  }
  for (const m of xml.matchAll(/<Domain>([0-9T:-]+Z)<\/Domain>/g)) out.push(Date.parse(m[1]));
  const times = [...new Set(out)].sort((a, b) => a - b);
  const newest = times.at(-1) ?? endMs;
  return times.filter((t) => t >= newest - windowMin * MIN);
}

export async function satFrameSet(source: SatSource, windowMin: number, endMs = Date.now()): Promise<FrameSet | null> {
  if (source.kind === "nowcoast") {
    const cfg = NC_SAT[source.product];
    const all = await nowcoastTimes("satellite", cfg.layer);
    const times = all.filter((t) => t >= (all.at(-1) ?? endMs) - windowMin * MIN);
    if (!times.length) return null;
    return {
      family: "sat", provider: `nc-${source.product}`, label: cfg.label, detail: cfg.detail, cadenceMin: 5,
      legend: cfg.legend,
      frames: times.map((t) => {
        const url = nowcoastWms("satellite", cfg.layer, t);
        return {
          key: `nc-${source.product}-${t}`, time: t,
          tiles: [source.product === "ir" ? enhancedIrUrl(url) : source.product === "wv" ? enhancedWvUrl(url) : url],
          tileSize: 512 as const, maxzoom: cfg.maxzoom, bounds: NC_SAT_BOUNDS,
          attribution: "Satellite: NOAA nowCOAST (GOES)",
        };
      }),
    };
  }
  const bird = SAT_FLEET[source.bird];
  const p = bird[source.product];
  const times = await gibsTimes(p, endMs, windowMin);
  if (!times.length) return null;
  const iso = (t: number) => new Date(t).toISOString().slice(0, 19) + "Z";
  return {
    family: "sat", provider: `gibs-${source.bird}-${source.product}`,
    label: `${bird.label}${source.product === "geocolor" && source.bird !== "himawari" ? " GeoColor" : source.product === "ir" ? " IR" : " visible"}`,
    detail: "full disk · 10-min",
    cadenceMin: 10,
    legend: source.product === "ir" ? IR_LEGEND : undefined,
    frames: times.map((t) => {
      const url = `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${p.layer}/default/${iso(t)}/${p.tms}/{z}/{y}/{x}.${p.ext}`;
      return {
        key: `gibs-${source.bird}-${source.product}-${t}`, time: t,
        tiles: [source.product === "ir" ? gibsIrUrl(url) : url],
        tileSize: 256 as const, maxzoom: p.zoom,
        attribution: `Satellite: NASA GIBS · ${bird.label}`,
      };
    }),
  };
}

/** GMGSI global water vapour (hourly), recoloured to match GOES band 8 and
 *  cut out under the 5-min GOES layer — it paints only where GOES can't. */
export async function globalWvFrameSet(windowMin: number, endMs = Date.now()): Promise<FrameSet | null> {
  const layer = "global_water_vapor_imagery_mosaic";
  const all = await nowcoastTimes("satellite", layer);
  // one extra hour, so the start of the loop still has a mosaic beneath it
  let times = all.filter((t) => t >= endMs - (windowMin + 60) * MIN);
  if (!times.length && all.length) times = all.slice(-1);
  if (!times.length) return null;
  return {
    family: "satbase", provider: "nc-gmgsi-wv", label: "Global water vapor", detail: "GMGSI mosaic · hourly · ~3 km",
    cadenceMin: 60, legend: WV_LEGEND,
    frames: times.map((t) => ({
      // the layer's default style stretches each tile to its own min/max —
      // borrow the fixed linear "goes-lir" ramp (see GREY_TO_C_GWV)
      key: `gmgsi-wv-${t}`, time: t, tiles: [enhancedGlobalWvUrl(nowcoastWms("satellite", layer, t, "goes-lir"))],
      tileSize: 512 as const, maxzoom: 6, attribution: "Satellite: NOAA/NESDIS GMGSI via nowCOAST",
    })),
  };
}

// ════════════════════════ OCEAN · LIGHTNING · 1-MIN · SURGE ════════════════════════

export type OceanLayer = "off" | "sst" | "ohc";

const SST_LEGEND: Legend = {
  unit: "°F",
  // nowCOAST's SPoRT ramp (°F); 80 °F ≈ 26.5 °C is the classic hurricane-fuel line
  stops: [
    { v: 60, color: "#50B200" }, { v: 68, color: "#C3E600" }, { v: 72, color: "#FFFF00" }, { v: 76, color: "#FFE600" },
    { v: 80, color: "#FFD000" }, { v: 84, color: "#FFB700" }, { v: 88, color: "#FC9700" }, { v: 92, color: "#F76F00" },
    { v: 96, color: "#F04800" },
  ],
  note: "sea-surface temp · ≥ 80 °F (26.5 °C) fuels hurricanes",
};

/** NASA SPoRT SST composite via nowCOAST (VIIRS/AVHRR, 6-hourly, newest). */
export async function sstFrameSet(): Promise<FrameSet | null> {
  const t = (await nowcoastTimes("sea_surface_temperature", "nasa_sport_sea_surface_temperature")).at(-1);
  if (t == null) return null;
  return {
    family: "ocean", provider: "nc-sst", label: "Sea-surface temperature", detail: "NASA SPoRT · 2 km", cadenceMin: 360,
    legend: SST_LEGEND,
    frames: [{
      key: `sst-${t}`, time: t, tiles: [nowcoastWms("sea_surface_temperature", "nasa_sport_sea_surface_temperature", t)],
      tileSize: 512, maxzoom: 8, attribution: "SST: NASA SPoRT via NOAA nowCOAST",
    }],
  };
}

const OHC_LEGEND: Legend = {
  unit: "kJ/cm²",
  stops: [
    { v: 10, color: "rgb(40,70,160)" }, { v: 25, color: "rgb(40,140,220)" }, { v: 40, color: "rgb(60,200,170)" },
    { v: 50, color: "rgb(120,220,80)" }, { v: 70, color: "rgb(250,230,50)" }, { v: 90, color: "rgb(250,150,30)" },
    { v: 110, color: "rgb(230,60,40)" }, { v: 140, color: "rgb(190,30,140)" }, { v: 180, color: "rgb(140,40,200)" },
  ],
  note: "heat above the 26 °C isotherm · ≥ 50 supports intensification",
};

/** AOML tropical-cyclone heat potential, server-rendered to one image. */
export async function ohcFrameSet(): Promise<FrameSet | null> {
  const d = await getJson<{ available: boolean; date?: string; bounds?: BBox; url?: string }>(apiUrl("/api/ocean/ohc"));
  if (!d?.available || !d.bounds || !d.url) return null;
  const [w, s, e, n] = d.bounds;
  return {
    family: "ocean", provider: "ohc", label: "Ocean heat content", detail: `AOML · ${d.date}`, cadenceMin: 1440,
    legend: { ...OHC_LEGEND, note: `${OHC_LEGEND.note} · ${d.date}` },
    frames: [{
      key: `ohc-${d.date}`, time: Date.parse(`${d.date}T12:00:00Z`),
      image: { url: apiUrl(d.url), coordinates: [[w, n], [e, n], [e, s], [w, s]] },
      attribution: "Ocean heat: NOAA AOML",
    }],
  };
}

const LIGHTNING_LEGEND: Legend = {
  unit: "strikes / 1000 km² / min",
  log: true,
  stops: [
    { v: 0.1, color: "#FFFFCC" }, { v: 0.5, color: "#FFFF00" }, { v: 1, color: "#FFD600" }, { v: 2.5, color: "#FFA400" },
    { v: 5, color: "#FF7800" }, { v: 10, color: "#FF4500" }, { v: 15, color: "#FF0000" }, { v: 25, color: "#BC0025" },
    { v: 50, color: "#CC0066" }, { v: 75, color: "#FF00FF" }, { v: 100, color: "#800080" },
  ],
  note: "15-min density · eyewall bursts often precede strengthening",
};

/** NLDN + GLD360 strike density via nowCOAST (15-min periods). */
export async function lightningFrameSet(windowMin: number): Promise<FrameSet | null> {
  const all = await nowcoastTimes("lightning_detection", "ldn_lightning_strike_density");
  const times = all.filter((t) => t >= (all.at(-1) ?? 0) - Math.max(windowMin, 15) * MIN);
  if (!times.length) return null;
  return {
    family: "lightning", provider: "nc-ltg", label: "Lightning", detail: "NLDN/GLD360 · 15-min", cadenceMin: 15,
    legend: LIGHTNING_LEGEND,
    frames: times.map((t) => ({
      key: `ltg-${t}`, time: t, tiles: [nowcoastWms("lightning_detection", "ldn_lightning_strike_density", t)],
      tileSize: 512, maxzoom: 9, attribution: "Lightning: NOAA nowCOAST (NLDN/GLD360)",
    })),
  };
}

/** GOES mesoscale sector over the storm — 1-minute frames rendered by the backend. */
export async function mesoFrameSet(lat: number, lon: number, product: SatProduct, windowMin: number): Promise<FrameSet | null> {
  const band = product === "wv" ? "wv" : product === "vis" ? "vis" : "ir";
  const d = await getJson<{
    available: boolean; reason?: string; sat?: string; sector?: string; label?: string;
    bounds?: BBox; cadence_min?: number; frames?: { time: string; url: string }[];
  }>(apiUrl(`/api/meso?lat=${lat.toFixed(2)}&lon=${lon.toFixed(2)}&band=${band}&minutes=${Math.min(180, Math.max(10, windowMin))}`));
  if (!d?.available || !d.bounds || !d.frames?.length) return null;
  const [w, s, e, n] = d.bounds;
  const coords: [[number, number], [number, number], [number, number], [number, number]] = [[w, n], [e, n], [e, s], [w, s]];
  return {
    family: "meso", provider: `meso-${d.sat}-${d.sector}-${band}`,
    label: `${d.label} ${d.sector === "M1" ? "Meso-1" : "Meso-2"}`,
    detail: `${band === "vis" ? "1 km" : "2 km"} · ${d.cadence_min ?? 1}-min`,
    cadenceMin: d.cadence_min ?? 1,
    legend: band === "ir" ? IR_LEGEND : band === "wv" ? WV_LEGEND : undefined,
    frames: d.frames.map((f) => ({
      key: `meso-${d.sat}-${d.sector}-${band}-${f.url.split("/").pop()!.split(".")[0]}-${w.toFixed(2)}`,
      time: Date.parse(f.time),
      image: { url: apiUrl(f.url), coordinates: coords },
      attribution: `Satellite: NOAA ${d.label} mesoscale (AWS Open Data)`,
    })),
  };
}

export interface SurgeInfo {
  available: boolean;
  adv?: string;
  current_adv?: number | null;
  bounds?: BBox;
  tiles?: string;
}

/** NHC Potential Storm Surge Flooding map as a raster layer (backend tiles). */
export function surgeFrameSet(info: SurgeInfo | null): FrameSet | null {
  if (!info?.available || !info.tiles || !info.bounds) return null;
  return {
    family: "surge", provider: `surge-${info.adv}`, label: "NHC surge flooding", detail: `advisory ${info.adv}`, cadenceMin: 360,
    frames: [{
      key: `surge-${info.tiles}`, time: 0, tiles: [apiUrl(info.tiles)], tileSize: 256, maxzoom: 12,
      bounds: info.bounds, attribution: "Storm surge: NOAA/NHC",
    }],
  };
}

// ── Futurecast: models' simulated IR / radar / rain along the storm's future path ──
// Server-rendered (data/futurecast.py). One image per forecast step, all on
// one box around the storm's path (a regional model's rain views add a sharp
// one of the impact zone). WPC is NOAA's official rainfall forecast; NBM its
// blend of ~30 models, with the chance of heavy rain.
export type FutureProduct = "ir" | "radar" | "rain" | "chance";
export type FutureModel = "hfsa" | "ecmwf" | "gfs" | "hrrr" | "rrfs" | "wpc" | "nbm";
export type FutureKind = "model" | "official" | "blend";
// NBM's chance view: the chance of more than this many inches in each period
export const FUTURE_CHANCE_IN = [1, 2, 3, 5, 8, 10] as const;
export const FUTURE_MODELS: { k: FutureModel; label: string; title: string; rainOnly?: boolean; blend?: boolean }[] = [
  { k: "hfsa", label: "HAFS-A", title: "NOAA's hurricane model — follows the storm, every 3 h to 5 days" },
  { k: "ecmwf", label: "ECMWF", title: "The European global model — every 3 h to 6 days. No simulated satellite or radar: IR is estimated from its cloud tops, radar is its rain rate" },
  { k: "gfs", label: "GFS", title: "NOAA's global model — every 3 h to 6 days: simulated satellite and radar, at coarser (~28 km) detail. A comparison for ECMWF" },
  { k: "hrrr", label: "HRRR", title: "NOAA's 3 km US model — hourly to 48 h (continental US only)" },
  { k: "rrfs", label: "RRFS", title: "NOAA's new 3 km Rapid Refresh Forecast System — hourly, to 84 h (US, Hawaii, Puerto Rico)" },
  { k: "wpc", label: "WPC", title: "NOAA Weather Prediction Center — the OFFICIAL rainfall forecast, day 1 to day 7 totals (US)", rainOnly: true },
  { k: "nbm", label: "NBM", title: "NOAA's National Blend of Models — a calibrated blend of ~30 models: rain totals at 2.5 km to 7 days, and the chance of heavy rain (US)", rainOnly: true, blend: true },
];
export interface FutureStep {
  fhr: number;
  time: number; // valid time, ms UTC
  label?: string; // WPC: the period ("Days 1–3")
  lat?: number; // the model's storm centre at this step (absent once it is a remnant)
  lon?: number;
  vmax?: number | null;
  pmin?: number | null;
  remnant: boolean; // the model's storm has decayed (the frames carry on: rain, moisture)
}
export interface FutureInfo {
  model: FutureModel;
  source: string; // the server's source key ("rrfs_hi")
  label: string; // "HAFS-A"
  official: boolean; // WPC: an official forecast, not a simulation
  kind: FutureKind; // model (simulated) | official (WPC) | blend (NBM)
  product: FutureProduct;
  thresholdIn: number | null; // the chance view: more than this many inches
  cycle: number; // run start (WPC: issued), ms UTC
  steps: FutureStep[];
  track: [number, number][]; // the model's storm, up to where it decays (none for HRRR/RRFS/WPC)
  focus: [number, number, number, number]; // w,s,e,n worth framing on screen
  fromFhr: number; // rain totals count from this step (≈ now)
  endFhr: number | null; // the step the storm becomes a remnant
  pointUrl: string; // rain at a spot: append &lat=&lon=
  citiesUrl: string; // the towns' totals (or chances) at every step
  detail: string;
  note: string | null; // a caveat about this product from this model
}

/** The step closest in valid time — switching models keeps the moment on screen. */
export function nearestStepIndex(steps: { time: number }[], time: number): number {
  let best = 0;
  for (let i = 1; i < steps.length; i++) {
    if (Math.abs(steps[i].time - time) < Math.abs(steps[best].time - time)) best = i;
  }
  return best;
}

const corners = ([w, s, e, n]: BBox): [[number, number], [number, number], [number, number], [number, number]] =>
  [[w, n], [e, n], [e, s], [w, s]];

export async function futureFrameSet(
  stormId: string, product: FutureProduct, model: FutureModel, chanceIn = 3,
): Promise<{ set: FrameSet; detail: FrameSet | null; info: FutureInfo } | { note: string }> {
  const asked = product === "chance" ? `chance${chanceIn}` : product; // the server names the threshold
  const d = await getJson<{
    available: boolean; reason?: string; model?: FutureModel; source?: string; label?: string; official?: boolean;
    kind?: FutureKind; cycle?: string; bounds?: BBox; focus?: BBox; from_fhr?: number; end_fhr?: number | null;
    point_url?: string; cities_url?: string; detail_bounds?: BBox | null; threshold_in?: number;
    legend?: Legend; detail?: string; note?: string | null; attribution?: string;
    frames?: { fhr: number; time: string; url: string; detail_url?: string; label?: string; remnant?: boolean; lat?: number; lon?: number; vmax?: number | null; pmin?: number | null }[];
    track?: [number, number][];
  }>(apiUrl(`/api/future/${stormId}?product=${asked}&model=${model}`));
  if (!d?.available || !d.bounds || !d.frames?.length) return { note: d?.reason ?? "No model run for this storm yet" };
  const cycle = Date.parse(d.cycle!);
  const hh = new Date(cycle).getUTCHours().toString().padStart(2, "0");
  const kind: FutureKind = d.kind ?? (d.official ? "official" : "model");
  const frames = d.frames;
  const key = (f: (typeof frames)[number], tag: string) =>
    `future${tag}-${d.source}-${d.cycle}-${asked}-${f.fhr}-${f.url.split("from=")[1] ?? ""}`;
  const attribution = d.attribution ?? `Simulated: ${d.label}`;
  const dBounds = d.detail_bounds;
  return {
    set: {
      family: "future", provider: `future-${d.source}-${d.cycle}-${asked}`,
      label: kind === "official" ? `${d.label} official · issued ${hh}Z` : kind === "blend" ? `${d.label} ${hh}Z blend` : `${d.label} ${hh}Z run`,
      detail: d.detail ?? "",
      cadenceMin: 60,
      legend: d.legend ?? (product === "ir" ? IR_LEGEND : NWS_LEGEND),
      frames: frames.map((f) => ({ key: key(f, ""), time: Date.parse(f.time), image: { url: apiUrl(f.url), coordinates: corners(d.bounds!) }, attribution })),
    },
    // the impact zone at the model's own resolution, drawn over the coarser whole region
    detail: dBounds && frames.every((f) => f.detail_url) ? {
      family: "futuredetail", provider: `futuredetail-${d.source}-${d.cycle}-${asked}`, label: "", detail: "", cadenceMin: 60,
      frames: frames.map((f) => ({ key: key(f, "detail"), time: Date.parse(f.time), image: { url: apiUrl(f.detail_url!), coordinates: corners(dBounds) }, attribution })),
    } : null,
    info: {
      model: d.model ?? model, source: d.source ?? model, label: d.label!, official: !!d.official, kind, product, cycle,
      thresholdIn: d.threshold_in ?? null,
      track: d.track ?? [], focus: d.focus ?? d.bounds, detail: d.detail ?? "", note: d.note ?? null,
      fromFhr: d.from_fhr ?? 0, endFhr: d.end_fhr ?? null, pointUrl: apiUrl(d.point_url ?? ""),
      citiesUrl: apiUrl(d.cities_url ?? ""),
      steps: d.frames.map((f) => ({
        fhr: f.fhr, time: Date.parse(f.time), label: f.label, lat: f.lat, lon: f.lon, vmax: f.vmax, pmin: f.pmin,
        remnant: !!f.remnant,
      })),
    },
  };
}
